import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { userState, vendorState } from '../state';
import type {
  EnumVendorModuleModuleType as EnumModuleModuleType,
  VendorModule as Module,
  User,
  Vendor,
} from '../lib/graphql';
import {
  isAdmin,
  isProcessor,
  isProducer,
  isVendorOwner,
} from '../lib/userHelpers';
import { ModuleId } from '../lib/modules/module-id.enum';

interface UserStateContextValue {
  user: User | undefined;
  vendor: Vendor | undefined;
  isAdmin: boolean;
  isProcessor: boolean;
  isProducer: boolean;
  isVendorOwner: boolean;
  isActiveModule: (moduleId: ModuleId) => boolean;
  getModuleConfig: <T = Record<string, unknown>>(
    moduleId: ModuleId,
  ) => T | null;
}

const UserStateContext = createContext<UserStateContextValue | undefined>(
  undefined,
);

interface UserStateProviderProps {
  children: ReactNode;
}

export function UserStateProvider({ children }: UserStateProviderProps) {
  const user = useRecoilValue(userState);
  const vendor = useRecoilValue(vendorState);

  const contextValue: UserStateContextValue = useMemo(
    () => ({
      user,
      vendor,
      isAdmin: isAdmin(user),
      isProcessor: isProcessor(user),
      isProducer: isProducer(user),
      isVendorOwner: isVendorOwner(user),
      isActiveModule: (moduleId: ModuleId) =>
        user?.active_vendor?.modules?.some(
          (m: Module) =>
            m?.moduleType === (moduleId as unknown as EnumModuleModuleType) &&
            m?.active,
        ) ?? false,
      getModuleConfig: <T = Record<string, unknown>,>(
        moduleId: ModuleId,
      ): T | null => {
        const module = user?.active_vendor?.modules?.find(
          (m: Module) =>
            m?.moduleType === (moduleId as unknown as EnumModuleModuleType),
        );
        return (module?.settings as T) ?? null;
      },
    }),
    [user, vendor],
  );

  return (
    <UserStateContext.Provider value={contextValue}>
      {children}
    </UserStateContext.Provider>
  );
}

export function useUserState(): UserStateContextValue {
  const context = useContext(UserStateContext);
  if (context === undefined) {
    throw new Error('useUserState must be used within a UserStateProvider');
  }
  return context;
}
